/**
 * React Query hooks for creative comparison
 * Provides data fetching for multiple creatives and their analyses
 */

import { useMutation, useQueries } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import type { PerformanceContextData } from '@/components/analysis';
import { analysesKeys } from '@/hooks/useAnalysis';
import { creativesKeys } from '@/lib/queryClient';
import { api } from '@/services/api';
import type { Creative, Analysis } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export interface ComparisonData {
  creative: Creative;
  analysis: Analysis | null;
  performanceContext: PerformanceContextData | null;
}

export interface UseComparisonResult {
  creativeIds: string[];
  data: ComparisonData[];
  isLoading: boolean;
  isError: boolean;
  errors: (Error | null)[];
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Parse creative IDs from URL query params
 */
export function useComparisonIds(): string[] {
  const [searchParams] = useSearchParams();
  const idsParam = searchParams.get('ids');

  if (!idsParam) {
    return [];
  }

  return idsParam.split(',').filter((id) => id.length > 0);
}

/**
 * Fetch multiple creatives by their IDs
 */
export function useCreativesById(creativeIds: string[]): {
  data: (Creative | undefined)[];
  isLoading: boolean;
  isError: boolean;
  errors: (Error | null)[];
} {
  const queries = useQueries({
    queries: creativeIds.map((id) => ({
      queryKey: creativesKeys.detail(id),
      queryFn: async () => {
        const creative = await api.get<Creative>(`/creatives/${id}`);
        return creative;
      },
      enabled: !!id,
      staleTime: 2 * 60 * 1000,
    })),
  });

  return {
    data: queries.map((q) => q.data),
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
    errors: queries.map((q) => q.error),
  };
}

/**
 * Fetch latest analyses for multiple creatives
 */
export function useAnalysesForCreatives(creativeIds: string[]): {
  data: (Analysis | null)[];
  isLoading: boolean;
  isError: boolean;
} {
  const queries = useQueries({
    queries: creativeIds.map((id) => ({
      queryKey: analysesKeys.list({ creativeId: id }),
      queryFn: async () => {
        const analyses = await api.get<Analysis[]>(
          `/creatives/${id}/analyses`
        );
        // Return the latest analysis (first in sorted array)
        return analyses.length > 0 ? analyses[0] : null;
      },
      enabled: !!id,
      staleTime: 2 * 60 * 1000,
      refetchOnMount: 'always' as const,  // Override global default for comparison view
    })),
  });

  return {
    data: queries.map((q) => q.data ?? null),
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
}

/**
 * Fetch performance context for multiple creatives
 */
export function usePerformanceContextForCreatives(creativeIds: string[]): {
  data: (PerformanceContextData | null)[];
  isLoading: boolean;
} {
  const queries = useQueries({
    queries: creativeIds.map((id) => ({
      queryKey: creativesKeys.performanceContext(id),
      queryFn: async () => {
        const data = await api.get<PerformanceContextData>(
          `/creatives/${id}/performance-context`
        );
        return data;
      },
      enabled: !!id,
      staleTime: 1 * 60 * 1000,
    })),
  });

  return {
    data: queries.map((q) => q.data ?? null),
    isLoading: queries.some((q) => q.isLoading),
  };
}

/**
 * Main comparison hook - combines creative and analysis data
 */
export function useComparison(): UseComparisonResult {
  const creativeIds = useComparisonIds();

  const {
    data: creatives,
    isLoading: creativesLoading,
    isError: creativesError,
    errors,
  } = useCreativesById(creativeIds);

  const {
    data: analyses,
    isLoading: analysesLoading,
    isError: analysesError,
  } = useAnalysesForCreatives(creativeIds);

  const {
    data: performanceContexts,
    isLoading: contextsLoading,
  } = usePerformanceContextForCreatives(creativeIds);

  // Combine creatives, analyses, and performance context
  const data: ComparisonData[] = creativeIds
    .map((_, index) => {
      const creative = creatives[index];
      if (!creative) {
        return null;
      }
      return {
        creative,
        analysis: analyses[index] ?? null,
        performanceContext: performanceContexts[index] ?? null,
      };
    })
    .filter((item): item is ComparisonData => item !== null);

  return {
    creativeIds,
    data,
    isLoading: creativesLoading || analysesLoading || contextsLoading,
    isError: creativesError || analysesError,
    errors,
  };
}

/**
 * Validate comparison selection
 */
export function validateComparisonSelection(
  count: number
): { valid: boolean; error?: string } {
  if (count < 2) {
    return { valid: false, error: 'Select at least 2 creatives to compare' };
  }
  if (count > 5) {
    return { valid: false, error: 'Maximum 5 creatives can be compared' };
  }
  return { valid: true };
}

// ============================================================================
// COMPARISON REPORT TYPES
// ============================================================================

export interface ComparisonReportResponse {
  report: string;
  creativeCount: number;
  creativeIds: string[];
}

export interface GenerateReportParams {
  projectId: string;
  creativeIds: string[];
}

// ============================================================================
// COMPARISON REPORT HOOK
// ============================================================================

/**
 * Hook to generate AI comparison report
 * Calls POST /projects/{projectId}/compare with creative IDs
 */
export function useGenerateComparisonReport() {
  return useMutation({
    mutationFn: async ({ projectId, creativeIds }: GenerateReportParams) => {
      const response = await api.post<ComparisonReportResponse>(
        `/projects/${projectId}/compare`,
        { creativeIds }
      );
      return response;
    },
  });
}
